import { createSlice } from '@reduxjs/toolkit';
import { DynamoConnector } from '../../DynamoConnector';

export const DEFAULT_BIN_KEY = 'bracket_4';

export const commanderCardStatsSlice = createSlice({
    name: 'commanderCardStats',
    initialState: {
        commanderIndexId: null,
        selectedBinKey: DEFAULT_BIN_KEY,
        responsesByBin: {},
        fetchingBins: {},
        isDisabled: false,
        // Health probe (mode=health) — tells the UI which bins have data and
        // whether the commander has been aggregated at all. Populated lazily
        // via fetchCommanderStatsHealth; consumers should treat `healthById`
        // as the source of truth for bin enable/disable decisions.
        healthById: {}, // { [commanderIndexId]: { status, eligible, deckCount, threshold, availableBins, lastSuccessfulAggregationAt } }
        healthFetching: {}, // { [commanderIndexId]: true }
    },
    reducers: {
        resetCommanderCardStats: (state, action) => {
            state.commanderIndexId = action.payload?.id ?? null;
            state.selectedBinKey = action.payload?.selectedBinKey ?? DEFAULT_BIN_KEY;
            state.responsesByBin = {};
            state.fetchingBins = {};
            state.isDisabled = false;
        },
        setSelectedBinKey: (state, action) => {
            state.selectedBinKey = action.payload;
        },
        setBinFetching: (state, action) => {
            const { binKey, isFetching } = action.payload;
            if (isFetching) {
                state.fetchingBins[binKey] = true;
            } else {
                delete state.fetchingBins[binKey];
            }
        },
        setBinResponse: (state, action) => {
            const { binKey, response } = action.payload;
            state.responsesByBin[binKey] = response;
            delete state.fetchingBins[binKey];
            state.isDisabled = false;
        },
        setCommanderCardStatsDisabled: (state) => {
            state.responsesByBin = {};
            state.fetchingBins = {};
            state.isDisabled = true;
        },
        setHealthFetching: (state, action) => {
            const { id, isFetching } = action.payload || {};
            if (!id) return;
            if (isFetching) {
                state.healthFetching[id] = true;
            } else {
                delete state.healthFetching[id];
            }
        },
        setHealthResponse: (state, action) => {
            const { id, data } = action.payload || {};
            if (!id) return;
            state.healthById[id] = data || null;
            delete state.healthFetching[id];
        },
    },
});

export const {
    resetCommanderCardStats,
    setSelectedBinKey,
    setBinFetching,
    setBinResponse,
    setCommanderCardStatsDisabled,
    setHealthFetching,
    setHealthResponse,
} = commanderCardStatsSlice.actions;

export const fetchCommanderCardStatsForBin = (id, binKey) => (dispatch, getState) => {
    if (!id || !binKey) return;

    const state = getState().commanderCardStats;

    if (state.commanderIndexId !== id) {
        dispatch(resetCommanderCardStats({ id, selectedBinKey: binKey }));
    }

    const latest = getState().commanderCardStats;
    if (latest.isDisabled) return;
    if (latest.responsesByBin[binKey]) return;
    if (latest.fetchingBins[binKey]) return;

    dispatch(setBinFetching({ binKey, isFetching: true }));

    DynamoConnector.getCommanderCardStats(id, binKey, ({ disabled, data }) => {
        if (getState().commanderCardStats.commanderIndexId !== id) return;

        if (disabled) {
            dispatch(setCommanderCardStatsDisabled());
            return;
        }

        dispatch(setBinResponse({ binKey, response: data }));
    });
};

export const selectCommanderBin = (id, binKey) => (dispatch) => {
    dispatch(setSelectedBinKey(binKey));
    dispatch(fetchCommanderCardStatsForBin(id, binKey));
};

export const fetchCommanderStatsHealth = (id, { force = false } = {}) => (dispatch, getState) => {
    if (!id) return;
    const state = getState().commanderCardStats;
    if (!force && state.healthById[id]) return;
    if (state.healthFetching[id]) return;

    dispatch(setHealthFetching({ id, isFetching: true }));

    DynamoConnector.getCommanderStatsHealth(id, ({ disabled, data }) => {
        if (disabled) {
            dispatch(setHealthResponse({ id, data: { status: 'disabled', availableBins: {} } }));
            return;
        }
        dispatch(setHealthResponse({ id, data }));
    });
};

export default commanderCardStatsSlice.reducer;
